/**
 * Module responsible to implement the Query String API for Rudderstack
 *
 * Use Query String API when there is a navigation to a new page and you want to send an tarck event
 *
 * Issue:
 * Rudderstack Query String API https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *  is not working as expected, we observed that when the track event is called on the next page the "traits" are not being sent
 * Rudderstack team indicates that the track call is performed on the Page Load and in order to have the traits available it needs to be after the SDK is ready
 * Rudderstack team probably will patch the feature to trigger the call when the SDK is ready() and not on Page Load
 *
 * For now we need to implement an in house solution to make it work
 * The solution will follow the same pattern as the Query String API so eventually we can remove it when the Rudderstack team fix the issue
 *
 */

import { QUERYSTRING_API } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as UrlUtils from '~/utils/url';

/**
 * Validate if the current URL contains the query string API parameters
 *
 * @private
 * @returns {boolean} - true if the URL contains the query string API parameters
 */
const _urlContainsQueryStringApi = () =>
  window.location.search.includes(QUERYSTRING_API.EVENT_NAME_PARAM);

/**
 * Call the track event with the event name and properties
 * Only call the track event if the user has performance consent
 *
 * @private
 * @param {string} eventName - The name of the event
 * @param {Object} eventProps - The properties of the event
 *
 * @returns {void}
 */
const _callTrackEvent = (eventName, eventProps) => {
  if (window.ALLBIRDS.OneTrust.hasPerformanceConsent()) {
    DataDogLogs.info('[Allbirds pixel] - RudderStack Query String API - sending analytics event', {
      eventName,
      eventProps,
    });

    window.rudderanalytics.track(eventName, eventProps);
  }
};

/**
 * On page load and SDK ready check if the URL contains the query string API parameters
 * - If it does, parse the URL, pull the data from query string and call the track api
 * - If the query string value is an object, deserialize it
 * - If the URL exceeds the max length, log an error to be monitored
 */
const executeQueryStringApi = () => {
  // Hook on page load and SDK ready callback to ensure traits are available
  document.addEventListener('DOMContentLoaded', () => {
    RudderstackUtils.rudderanalyticsReadyCallback(() => {
      DataDogLogs.info(
        '[Allbirds pixel] - RudderStack Query String API - rudderstack ready event',
        {
          queryStringApi: _urlContainsQueryStringApi(),
        },
      );

      if (_urlContainsQueryStringApi()) {
        try {
          const eventProps = {};
          const url = new URL(window.location.href);
          const params = url.searchParams; // Automatically handles URL decoding
          const eventName = params.get(QUERYSTRING_API.EVENT_NAME_PARAM);

          // Iterate over each parameter entry [key, value]
          for (const [key, value] of params.entries()) {
            // Check if the parameter key starts with the specified prefix
            if (key.startsWith(QUERYSTRING_API.EVENT_PROP_PREFIX_PARAM)) {
              // Extract the suffix part of the key (the part after the prefix)
              const propertyName = key.substring(QUERYSTRING_API.EVENT_PROP_PREFIX_PARAM.length);

              // Default value is the value from the URL
              let propertyValue = value;

              // If value is an object deserialize it
              if (value.startsWith(QUERYSTRING_API.PROP_TYPE_OBJECT_PREFIX)) {
                // Remove the prefix type flag
                const objectValue = value.replace(QUERYSTRING_API.PROP_TYPE_OBJECT_PREFIX, '');

                // Deserialize the object
                propertyValue = JSON.parse(objectValue);
              }

              // Add the extracted suffix as the key and the parameter value
              if (propertyName) {
                eventProps[propertyName] = propertyValue;
              }
            }
          }

          // If OneTrust is ready, call track event
          if (window.ALLBIRDS.OneTrust.isOneTrustActiveGroupsReady()) {
            _callTrackEvent(eventName, eventProps);
          } else {
            // If OneTrust is not ready, push track event into a pool to be triggered once OneTrust is ready
            window.ALLBIRDS.OneTrust.onConsentChange(() => {
              _callTrackEvent(eventName, eventProps);
            });
          }

          // Clean up the URL by removing the query string parameters
          const queryStringApiParamToDelete = [
            QUERYSTRING_API.EVENT_NAME_PARAM,
            ...Object.keys(eventProps).map(
              (key) => `${QUERYSTRING_API.EVENT_PROP_PREFIX_PARAM}${key}`,
            ),
          ];
          UrlUtils.removeQueryString(queryStringApiParamToDelete);
        } catch (error) {
          DataDogLogs.error(
            '[Allbirds pixel] - RudderStack Query String API - executeQueryStringApi - error parsing',
            { currentUrl: window.location.href },
            error,
          );
        }
      }
    });
  });
};

export { executeQueryStringApi };
